import React, { useEffect } from 'react'
import { RouterProvider } from 'react-router-dom'
import Cookies from 'js-cookie'
import posthog from 'posthog-js'
import { PostHogProvider } from 'posthog-js/react'

import { useIsMutating, useIsFetching } from '@tanstack/react-query'

import Loading from '@components/Loading'

import router from './Routes'

import use3rdParties from '@helpers/hooks/use3rdParties'

import { loadableMutations, loadableQueries } from '@queries/Keys'

import styles from './app.module.css'

// PostHog is initialised lazily inside App so it never blocks the initial
// module evaluation / first render.
let posthogInitialised = false

const Loader = () => {
  const isMutating = useIsMutating({
    predicate: (mutation) => {
      const key = mutation?.options?.mutationKey?.[0]
      return loadableMutations.includes(key as string)
    },
  })
  const isFetching = useIsFetching({
    predicate: (query) => {
      const key = Array.isArray(query.queryKey) ? query.queryKey[0] : query.queryKey
      return loadableQueries.includes(key)
    },
  })

  return <Loading loading={!!(isMutating || isFetching)} />
}

/// MOBILE BROWSER HEIGHT HACK
const vh = window.innerHeight * 0.01
document.documentElement.style.setProperty('--vh', `${vh}px`)

window.addEventListener('resize', () => {
  const vh = window.innerHeight * 0.01
  document.documentElement.style.setProperty('--vh', `${vh}px`)
})
///

const App = () => {
  use3rdParties()

  useEffect(() => {
    if (!posthogInitialised && import.meta.env.VITE_POSTHOG_API_KEY) {
      posthogInitialised = true
      posthog.init(import.meta.env.VITE_POSTHOG_API_KEY, {
        api_host: import.meta.env.VITE_POSTHOG_HOST || 'https://app.posthog.com',
        capture_pageview: true,
        capture_pageleave: true,
        autocapture: true,
        session_recording: {
          recordCrossOriginIframes: true,
        },
      })
    }
  }, [])

  useEffect(() => {
    const token = localStorage.getItem('token')
    const refreshToken = localStorage.getItem('refreshToken')
    if (token && refreshToken) {
      Cookies.set('token', token, { expires: 7 })
      Cookies.set('refreshToken', refreshToken, { expires: 7 })
    }
  }, [])

  return (
    <PostHogProvider client={posthog}>
      <div className={styles.app}>
        <RouterProvider router={router} />
        <Loader />
      </div>
    </PostHogProvider>
  )
}

export default App